import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeuix/themes';
import {
  nspAmber,
  nspCrimson,
  nspMint,
  nspMist,
  nspOcean,
  nspSky,
  nspSlate,
  nspSolar,
} from './colour-palettes.constants';
import { buttonPreset } from './component-presets/button.preset';
import { checkboxPreset } from './component-presets/checkbox.preset';
import { chipPreset } from './component-presets/chip.preset';
import { datePickerPreset } from './component-presets/date-picker.preset';
import { inputTextPreset } from './component-presets/input-text.preset';
import { listboxPreset } from './component-presets/listbox.preset';
import { multiSelectPreset } from './component-presets/multi-select.preset';
import { popoverPreset } from './component-presets/popover.preset';
import { progressBarPreset } from './component-presets/progress-bar.preset';
import { radioButtonPreset } from './component-presets/radio-button.preset';
import { selectPreset } from './component-presets/select.preset';
import { splitButtonPreset } from './component-presets/split-button.preset';
import { tieredMenuPreset } from './component-presets/tiered-menu.preset';
import { toggleButtonPreset } from './component-presets/toggle-button.preset';
import { toggleSwitchPreset } from './component-presets/toggle-switch.preset';

export const nspOceanPreset = definePreset(Aura, {
  primitive: {
    nspSky,
    nspOcean,
    nspSlate,
    nspMint,
    nspSolar,
    nspAmber,
    nspCrimson,
    nspMist,
  },
  semantic: {
    borderRadius: '4px',
    focusRing: {
      width: '1px',
      style: 'solid',
      color: '{nspSky.300}',
      offset: '0',
    },
    primary: {
      50: '{nspOcean.50}',
      100: '{nspOcean.100}',
      200: '{nspOcean.200}',
      300: '{nspOcean.300}',
      400: '{nspOcean.400}',
      500: '{nspOcean.500}',
      600: '{nspOcean.600}',
      700: '{nspOcean.700}',
      800: '{nspOcean.800}',
      900: '{nspOcean.900}',
      950: '{nspOcean.950}',
    },
    colorScheme: {
      dark: {
        primary: {
          color: '{nspOcean.400}',
          contrastColor: '{nspSlate.0}',
          hoverColor: '{nspOcean.300}',
          activeColor: '{nspOcean.600}',
        },
        highlight: {
          background: '{nspSky.500}',
          focusBackground: '{nspSky.300}',
          color: '{nspSlate.0}',
          focusColor: '{nspSlate.0}',
        },
        surface: {
          0: '#000000',
          50: '{nspSlate.950}',
          100: '{nspSlate.900}',
          200: '{nspSlate.800}',
          300: '{nspSlate.700}',
          400: '{nspSlate.600}',
          500: '{nspSlate.500}',
          600: '{nspSlate.400}',
          700: '{nspSlate.300}',
          800: '{nspSlate.200}',
          900: '{nspSlate.100}',
          950: '{nspSlate.50}',
        },
        formField: {
          background: '{nspSlate.800}',
          disabledBackground: '{nspSlate.700}',
          filledBackground: '{nspSlate.800}',
          filledFocusBackground: '{nspSlate.700}',
          borderColor: '{nspSlate.200}',
          hoverBorderColor: '{nspSlate.50}',
          focusBorderColor: '{nspSky.300}',
          invalidBorderColor: '{nspCrimson.400}',
          color: '{nspSlate.0}',
          disabledColor: '{nspSlate.100}',
          placeholderColor: 'var(--text-input-input-placeholder)',
          floatLabelColor: '{nspSlate.100}',
          floatLabelFocusColor: '{nspSky.300}',
          floatLabelInvalidColor: '{nspCrimson.400}',
          floatLabelActiveColor: '{nspSlate.100}',
          iconColor: 'var(--text-input-input-unit)',
          shadow: 'none',
        },
        list: {
          option: {
            focusBackground: '{nspSky.300}',
          },
        },
        overlay: {
          select: {
            background: '{nspSky.900}',
          },
        },
      },
      light: {
        primary: {
          color: '{nspOcean.400}',
          contrastColor: '{nspSlate.0}',
          hoverColor: '{nspOcean.500}',
          activeColor: '{nspOcean.600}',
        },
        highlight: {
          background: '{nspOcean.600}',
          focusBackground: '{nspOcean.400}',
          color: '{nspOcean.700}',
          focusColor: '{nspOcean.700}',
        },
        surface: {
          0: '{nspSlate.0}',
          50: '{nspMist.25}',
          100: '{nspMist.50}',
          200: '{nspMist.200}',
          300: '{nspMist.300}',
          400: '{nspMist.400}',
          500: '{nspMist.500}',
          600: '{nspMist.600}',
          700: '{nspMist.700}',
          800: '{nspMist.800}',
          900: '{nspMist.900}',
          950: '{nspMist.950}',
        },
        formField: {
          background: '{nspSlate.0}',
          disabledBackground: '{nspMist.50}',
          disabledBorderColor: 'red',
          filledBackground: '{nspMist.50}',
          filledFocusBackground: '{nspSlate.0}',
          borderColor: '{nspMist.300}',
          hoverBorderColor: '{nspOcean.400}',
          focusBorderColor: '{nspSky.300}',
          invalidBorderColor: '{nspCrimson.400}',
          color: '{nspMist.900}',
          disabledColor: '{nspMist.400}',
          placeholderColor: 'var(--text-input-input-placeholder)',
          floatLabelColor: '{nspMist.500}',
          floatLabelFocusColor: '{nspOcean.400}',
          floatLabelInvalidColor: '{nspCrimson.400}',
          floatLabelActiveColor: '{nspMist.500}',
          iconColor: 'var(--text-input-input-unit)',
          shadow: 'none',
        },
        list: {
          option: {
            focusBackground: '{nspOcean.400}',
          },
        },
      },
    },
  },
  components: {
    button: buttonPreset,
    toggleswitch: toggleSwitchPreset,
    radiobutton: radioButtonPreset,
    inputtext: inputTextPreset,
    togglebutton: toggleButtonPreset,
    checkbox: checkboxPreset,
    chip: chipPreset,
    select: selectPreset,
    multiselect: multiSelectPreset,
    datepicker: datePickerPreset,
    listbox: listboxPreset,
    popover: popoverPreset,
    progressbar: progressBarPreset,
    splitbutton: splitButtonPreset,
    tieredmenu: tieredMenuPreset,
    focusring: {
      width: '2px',
      style: 'solid',
      color: '{nspSky.300}',
      offset: '2px',
    },
  },
});
